import styled, { keyframes } from 'styled-components'
import { theme } from 'styles/theme'
import { GhostButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'

export enum UpdateRequestState {
    Pending = 'pending',
    InProgress = 'in progress',
    Succeeded = 'succeeded',
    Failed = 'failed',
}

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`

const SpinningLoader = styled(SvgIcons.LoadingCircleSvg)`
    animation: ${spin} 2s linear infinite;
`

const stateIcons = {
    [UpdateRequestState.Pending]: SvgIcons.CircleOutlineSvg,
    [UpdateRequestState.InProgress]: SpinningLoader,
    [UpdateRequestState.Succeeded]: SvgIcons.CheckCircleOutlineSvg,
    [UpdateRequestState.Failed]: SvgIcons.XCircleSvg,
}

const stateColors = {
    [UpdateRequestState.Pending]: theme.colors.SecondaryGrey,
    [UpdateRequestState.InProgress]: theme.colors.SecondaryGrey,
    [UpdateRequestState.Succeeded]: '#009951',
    [UpdateRequestState.Failed]: theme.colors.Red,
}

export const UPDATE_REQUEST_ROW_HEIGHT = 'calc(2.25rem + 3px)'

const Row = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 0.5em;
    block-size: ${UPDATE_REQUEST_ROW_HEIGHT};
    font-size: calc(${theme.fontSizes.small});
    color: ${theme.colors.MainBlack};
`

// Centers the state icon on the first text line, so it stays aligned to the top when there is a message line
const FirstLineAligned = styled.span`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    block-size: 1lh;
`

// Holds the state icon, for the status tooltip
const StateIconWrapper = styled(FirstLineAligned)`
    > svg {
        inline-size: 16px;
        block-size: 16px;
    }
`

// The vehicle id and the profile text, with the message below them
const Texts = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.2em;
    min-inline-size: 0;
`

// The vehicle id, the profile text and the dismiss button
const TopLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
`

const PrimaryText = styled.span<{ $color?: string }>`
    font-weight: ${theme.fontWeights.semiBold};
    color: ${({ $color }) => $color ?? 'inherit'};
    white-space: nowrap;
`

const VehicleIdText = styled(PrimaryText)`
    flex-shrink: 0;
    margin-inline-end: calc(1rem - 0.5em); // Together with the gap, keeps 1rem from the profile text
`

const ProfileText = styled(PrimaryText)`
    margin-inline-start: auto;
    overflow: hidden;
    text-overflow: ellipsis;
`

const MessageText = styled.span`
    color: ${theme.colors.Red};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const DismissButton = styled(GhostButton)`
    --size: 12px;
    --block-size: 12px;
    flex-shrink: 0;
    align-self: flex-start;
`

// Keeps the texts aligned with the rows that have a dismiss button
const DismissButtonPlaceholder = styled.span`
    flex-shrink: 0;
    inline-size: 12px;
`

const getProfileText = (state: UpdateRequestState, profileTitle: string) => {
    switch (state) {
        case UpdateRequestState.Pending:
            return `Pending update to ${profileTitle}`
        case UpdateRequestState.InProgress:
            return `Updating to ${profileTitle}`
        case UpdateRequestState.Succeeded:
            return `Updated to ${profileTitle}`
        case UpdateRequestState.Failed:
            return `Failed ${profileTitle}`
    }
}

export interface UpdateRequestRowProps {
    vehicleId: number
    message: string | null
    profileTitle: string
    state: UpdateRequestState
    statusText: string
    // Without it, the request can't be dismissed
    dismiss?: { title: string, isDismissing: boolean, onDismiss: VoidFunction }
}

// A profile update request of a vehicle, in UpdateRequestsViewer
export const UpdateRequestRow = ({ vehicleId, message, profileTitle, state, statusText, dismiss }: UpdateRequestRowProps) => {
    const StateIcon = stateIcons[state]
    const profileText = getProfileText(state, profileTitle)
    return (
        <Row>
            <StateIconWrapper {...{ role: 'img', 'aria-label': statusText, title: statusText }}>
                <StateIcon {...{ 'aria-hidden': true }} />
            </StateIconWrapper>
            <Texts>
                <TopLine>
                    <VehicleIdText>{ vehicleId }</VehicleIdText>
                    <ProfileText {...{ $color: stateColors[state], title: profileText }}>{ profileText }</ProfileText>
                    { dismiss
                        ? <DismissButton {...{ text: dismiss.title, 'aria-label': dismiss.title, isDisabled: dismiss.isDismissing, onClick: dismiss.onDismiss }}>
                            <SvgIcons.CloseXSvg />
                        </DismissButton>
                        : <DismissButtonPlaceholder />
                    }
                </TopLine>
                { message && <MessageText {...{ title: message }}>{ message }</MessageText> }
            </Texts>
        </Row>
    )
}
